/**
 * Forwarding Emails Section
 * Manages additional email addresses that can forward job alerts to this account.
 */
import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Loader2, X, Mail, Plus, RefreshCw, Info } from 'lucide-react';

interface ForwardingEmail {
  email: string;
  added_at: string;
}

export function ForwardingEmailsSection() {
  const [emails, setEmails] = useState<ForwardingEmail[]>([]);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [maxEmails, setMaxEmails] = useState(5);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadEmails();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  async function loadEmails() {
    try {
      const data = await api.getForwardingEmails();
      setEmails(data.forwarding_emails || []);
      setPendingEmail(data.pending_email || null);
      setMaxEmails(data.max_emails || 5);
    } catch {
      // Ignore load errors on mount
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setError(null);
    setAdding(true);
    try {
      const data = await api.addForwardingEmail(newEmail.trim());
      setPendingEmail(data.pending_email);
      setNewEmail('');
      setSuccess('Verification code sent! Check your inbox.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add email');
    } finally {
      setAdding(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!verificationCode.trim()) return;
    setError(null);
    setVerifying(true);
    try {
      const data = await api.verifyForwardingEmail(verificationCode.trim());
      setEmails(data.forwarding_emails || []);
      setPendingEmail(null);
      setVerificationCode('');
      setSuccess('Email verified and added!');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  }

  async function handleResend() {
    setError(null);
    setResending(true);
    try {
      await api.resendForwardingCode();
      setSuccess('New verification code sent!');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  }

  async function handleRemove(email: string) {
    setError(null);
    setRemoving(email);
    try {
      const data = await api.removeForwardingEmail(email);
      setEmails(data.forwarding_emails || []);
      setSuccess('Email removed');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to remove email');
    } finally {
      setRemoving(null);
    }
  }

  async function handleCancelPending() {
    if (!pendingEmail) return;
    setError(null);
    setRemoving(pendingEmail);
    try {
      await api.removeForwardingEmail(pendingEmail);
      setPendingEmail(null);
      setVerificationCode('');
    } catch {
      // Ignore
    } finally {
      setRemoving(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-2.5 bg-blue-50 rounded-lg">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700">
          Add email addresses that you forward job alerts from. When you forward a job alert
          email to <strong>jobs@skillsnap.me</strong>, we match the sender to your account.
          Add emails here so alerts forwarded from other inboxes (work, recruiter, etc.) are
          also recognized as yours.
        </p>
      </div>

      {error && (
        <div className="p-2 bg-red-50 text-red-700 rounded text-xs">{error}</div>
      )}
      {success && (
        <div className="p-2 bg-green-50 text-green-700 rounded text-xs">{success}</div>
      )}

      {/* Verified emails list */}
      {emails.length > 0 && (
        <div className="space-y-1">
          {emails.map((fe) => (
            <div key={fe.email} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm text-gray-700">{fe.email}</span>
              </div>
              <button
                onClick={() => handleRemove(fe.email)}
                disabled={removing === fe.email}
                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                title="Remove email"
              >
                {removing === fe.email ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <X className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pending verification */}
      {pendingEmail && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-amber-800">
              Verification pending for <strong>{pendingEmail}</strong>
            </span>
            <button
              onClick={handleCancelPending}
              className="text-xs text-gray-500 hover:text-red-500"
            >
              Cancel
            </button>
          </div>
          <form onSubmit={handleVerify} className="flex gap-2">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="submit"
              disabled={verifying || !verificationCode.trim()}
              className="px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify'}
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded"
              title="Resend code"
            >
              {resending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
            </button>
          </form>
        </div>
      )}

      {/* Add new email form */}
      {!pendingEmail && emails.length < maxEmails && (
        <form onSubmit={handleAdd} className="flex gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Add forwarding email address"
            className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            type="submit"
            disabled={adding || !newEmail.trim()}
            className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 disabled:opacity-50"
          >
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Add
          </button>
        </form>
      )}

      {emails.length >= maxEmails && !pendingEmail && (
        <p className="text-xs text-gray-500">Maximum of {maxEmails} forwarding emails reached.</p>
      )}
    </div>
  );
}
